import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["link", "content", "tabsList"];
  static values = {
    param: String,
    defaultSelectionLast: { type: Boolean, default: false },
    preserveHeight: { type: Boolean, default: true },
    disableUrls: { type: Boolean, default: false },
  };
  static tabs = [];
  static observer;

  connect() {
    this.tabs = this.linkTargets.map(tab => tab.dataset.tabId)
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const tabId = urlParams.get(this.paramValue);
    let activeTab;

    this.linkTargets.forEach((el) => {
      if (el.classList.contains("active")) {
        activeTab = el;
      }
    });

    if (activeTab) {
      this.activateTab(activeTab.dataset.tabId);
    } else if (this.disableUrlsValue) {
      this.chooseActiveTabBasedOnOrder();
    } else if (this.tabs.includes(tabId)) {
      this.activateTab(tabId)
    } else {
      this.chooseActiveTabBasedOnOrder();
    }

    if (this.hasTabsListTarget) {
      this.installMutationObserver();
    }
  }

  chooseActiveTabBasedOnOrder() {
    if (this.defaultSelectionLastValue) {
      this.activateTab(this.tabs[this.tabs.length - 1])
    } else {
      this.activateTab(this.tabs[0])
    }
  }

  installMutationObserver() {
    this.observer = new MutationObserver((mutationList, _observer) => {
      for (const mutation of mutationList) {
        if (mutation.type === 'childList' && mutation.removedNodes[0]) {
          this.tabs = this.linkTargets.map(tab => tab.dataset.tabId)
          this.activateTab(this.tabs[this.tabs.length - 1])
        }
      }
    });

    this.observer.observe(this.tabsListTarget, { childList: true, attributes: false, subtree: false });
  }

  activateTab(tabId) {
    const height = this.element.offsetHeight || 0
    const minHeight = parseInt(this.element.style.minHeight) || 0
    let activeTab;

    if (this.preserveHeightValue && minHeight < height) {
      this.element.style.minHeight = `${height}px`
    }

    this.linkTargets.forEach((el) => el.classList.remove('active'));
    this.linkTargets.forEach((el) => {
      if (el.dataset.tabId == tabId) {
        el.classList.add('active')
      }
    })

    this.contentTargets.forEach((el) => el.classList.remove('active'));
    this.contentTargets.forEach((el) => {
      if (el.dataset.tabId == tabId) {
        el.classList.add('active')
      }
    })

    this.linkTargets.forEach((el) => {
      if (el.classList.contains("active")) {
        activeTab = el;
      }
    });

    this.updateAriaTabs();
    this.updateCurrentLinks();

    const event = new CustomEvent('tabActivated', { detail: activeTab });
    this.element.dispatchEvent(event);
  }

  // Views that give their links role="tab" follow the ARIA tabs pattern:
  // the selected tab is announced and is the only one in the Tab order.
  updateAriaTabs() {
    this.ariaTabs().forEach((tab) => {
      const selected = tab.classList.contains("active");
      tab.setAttribute("aria-selected", selected);
      tab.tabIndex = selected ? 0 : -1;
    });
  }

  // Plain tab links have no selected state, so mark the active one as current instead.
  updateCurrentLinks() {
    this.linkTargets.filter((el) => el.getAttribute("role") !== "tab").forEach((link) => {
      if (link.classList.contains("active")) {
        link.setAttribute("aria-current", "true");
      } else {
        link.removeAttribute("aria-current");
      }
    });
  }

  ariaTabs() {
    return this.linkTargets.filter((el) => el.getAttribute("role") === "tab");
  }

  navigate(event) {
    const tabs = this.ariaTabs();
    const index = tabs.indexOf(event.target.closest('[role="tab"]'));
    const nextIndex = { ArrowLeft: index - 1, ArrowRight: index + 1, Home: 0, End: tabs.length - 1 }[event.key];

    if (index === -1 || nextIndex === undefined) return;

    event.preventDefault();
    const nextTab = tabs[(nextIndex + tabs.length) % tabs.length];
    this.select(nextTab.dataset.tabId);
    nextTab.focus();
  }

  change(event) {
    event.preventDefault();
    this.select(event.currentTarget.dataset.tabId);
  }

  select(tabId) {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    if (this.tabs.includes(tabId)) {
      this.activateTab(tabId);
      if (!this.disableUrlsValue) {
        urlParams.set(this.paramValue, tabId);
        history.replaceState("", "", "?" + urlParams.toString());
      }
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}
